import {
  createContext, useContext, useState, useEffect, useCallback,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient, { setAccessToken, ApiError } from '@/lib/api-client';
import { queryClient } from '@/lib/api/queryClient';
import { disableGoogleAutoSelect } from '@/services/authService';
import type { KycProgress } from '@/hooks/useKyc';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  partnerUuid:   string;
  email:         string;
  isAdmin:       boolean;
  kycStatus?:    string;
  walletState?:  string;
  accountState?: string;   // pending | active | suspended | closed
  verificationTicket?: string;
  verificationExpiresAt?: string;
  kycProgress?:  KycProgress;
}

interface AuthContextValue {
  user:              AuthUser | null;
  isAuthenticated:   boolean;
  isLoading:         boolean;
  login:             (email: string, password: string) => Promise<void>;
  loginWithGoogle:   (credential: string) => Promise<void>;
  register:          (data: RegisterInput) => Promise<void>;
  logout:            () => Promise<void>;
  refreshUser:       () => Promise<AuthUser | null>;
}

export interface RegisterInput {
  name:            string;
  email:           string;
  password:        string;
  countryCode:     string;
  investorType?:   string;
  termsAccepted:   true;
  marketingOptin?: boolean;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<AuthUser | null>(null);
  const [isLoading, setLoading] = useState(true);
  const navigate               = useNavigate();

  // Récupère l'utilisateur courant depuis le backend (appelé au boot + après refresh)
  const fetchMe = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const data = await apiClient.get<AuthUser & { accountState?: string }>('/auth/me');
      return { ...data, isAdmin: Boolean(data.isAdmin) };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  }, []);

  // Enrichit le user avec son statut KYC et wallet
  const fetchUserDetails = useCallback(async (base: AuthUser): Promise<AuthUser> => {
    try {
      const [kycStatus, walletInfo] = await Promise.allSettled([
        apiClient.get<{ state: string; progress?: KycProgress }>('/kyc/status'),
        apiClient.get<{ state: string } | null>('/wallet'),
      ]);
      return {
        ...base,
        kycStatus:   kycStatus.status  === 'fulfilled' ? kycStatus.value?.state  : undefined,
        kycProgress: kycStatus.status  === 'fulfilled' ? kycStatus.value?.progress : undefined,
        walletState: walletInfo.status === 'fulfilled' ? walletInfo.value?.state : undefined,
      };
    } catch {
      return base;
    }
  }, []);

  // Rafraîchit l'utilisateur (appelé depuis les pages après changement de KYC/wallet
  // et immédiatement après un handoff GreenCube → SolarCell).
  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    setLoading(true);
    try {
      const base = await fetchMe();
      if (!base) {
        setUser(null);
        return null;
      }
      const detailed = await fetchUserDetails(base);
      setUser(detailed);
      return detailed;
    } finally {
      setLoading(false);
    }
  }, [fetchMe, fetchUserDetails]);

  // Bootstrap : essaie de restaurer la session via le cookie de refresh
  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        // Tente un refresh pour obtenir un nouveau access_token
        await apiClient.post('/auth/refresh');
        const base = await fetchMe();
        if (base) {
          const detailed = await fetchUserDetails(base);
          setUser(detailed);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [fetchMe, fetchUserDetails]);

  // ── Méthodes exposées ────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiClient.post<{ partnerUuid: string; email: string }>(
      '/auth/login', { email, password },
    );
    const base = await fetchMe() ?? {
      partnerUuid: result.partnerUuid,
      email:       result.email,
      isAdmin:     false,
    };
    const detailed = await fetchUserDetails(base);
    setUser(detailed);
  }, [fetchMe, fetchUserDetails]);

  const register = useCallback(async (data: RegisterInput) => {
    const result = await apiClient.post<{
      partnerUuid: string;
      email: string;
      verificationTicket: string;
      verificationExpiresAt: string;
    }>(
      '/auth/register', data,
    );
    const base = {
      partnerUuid: result.partnerUuid,
      email: result.email,
      isAdmin: false,
      verificationTicket: result.verificationTicket,
      verificationExpiresAt: result.verificationExpiresAt,
    };
    sessionStorage.setItem('emailVerificationTicket', result.verificationTicket);
    sessionStorage.setItem('emailVerificationEmail', result.email);
    setUser(base);
  }, []);

  // `credential` est le JWT ID token émis par Google Identity Services
  // (récupéré par GoogleOAuthButton via le bouton officiel Google). Le
  // backend le vérifie directement auprès de Google — plus d'intermédiaire
  // Firebase dans la chaîne de confiance.
  const loginWithGoogle = useCallback(async (credential: string) => {
    const backendResult = await apiClient.post<{ partnerUuid: string; email: string }>(
      '/auth/google',
      { idToken: credential },
    );

    const base = await fetchMe() ?? {
      partnerUuid: backendResult.partnerUuid,
      email:       backendResult.email,
      isAdmin:     false,
    };
    const detailed = await fetchUserDetails(base);
    setUser(detailed);
    if (detailed.kycStatus === 'validated' ||
        detailed.kycStatus === 'submitted' ||
        detailed.kycStatus === 'under_review') {
      navigate('/tableau-de-bord');
      return;
    }

    const progress = detailed.kycProgress;
    const nextRoute = !progress?.personalInfo ? '/kyc/informations-personnelles'
      : !progress.identity ? '/kyc/identite'
      : !progress.selfie ? '/kyc/selfie'
      : !progress.proofOfAddress ? '/kyc/justificatif-domicile'
      : !progress.sourceOfFunds ? '/kyc/source-des-fonds'
      : '/kyc/revue';
    navigate(nextRoute);
  }, [fetchMe, fetchUserDetails, navigate]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // silent — logout même en cas d'erreur réseau
    }
    // Empêche Google de re-proposer silencieusement le compte au prochain chargement
    disableGoogleAutoSelect();
    setAccessToken(null);
    setUser(null);
    queryClient.clear();
    navigate('/connexion');
  }, [navigate]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      loginWithGoogle,
      register,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé à l\'intérieur de <AuthProvider>');
  return ctx;
}
